﻿import {AbstractPointSnapStrategy} from './AbstractPointSnapStrategy';
import {PointSnapStrategyType} from './PointSnapStrategyType';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import type {SketchLineObject} from '../../mesh/sketch/SketchLineObject';
import type {SketchArcObject} from '../../mesh/sketch/SketchArcObject';
import {isSketchLineObject} from '../../mesh/guards/isSketchLineObject';
import {isSketchArcObject} from '../../mesh/guards/isSketchArcObject';
import {isSketchCircleObject} from '../../mesh/guards/isSketchCircleObject';
import {isSketchEllipseObject} from '../../mesh/guards/isSketchEllipseObject';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import {QuickSnapMode} from './QuickSnapMode';
import {GeometryInteractionState} from '../interactionState/GeometryInteractionState';



/**
 * 中点捕捉策略（含边缘悬停逻辑与快速捕捉模式）。
 * 说明：
 * 1. 普通模式：检测鼠标是否悬停在线段/圆弧/圆/椭圆边缘上；线段与圆弧显示并捕捉真实中点，
 *    鼠标移出后继续粘滞显示中点，直到移入其他几何体；完整圆与完整椭圆只负责终止粘滞状态。
 * 2. 快速捕捉模式（QuickSnapMode.Midpoint）：
 *    - 全局搜索最近的线段/圆弧（无距离限制），高亮对应边线；
 *    - 直接显示并捕捉中点，权重 200 确保必定胜出；
 *    - 圆/椭圆在快速中点捕捉中不参与（无确定中点）。
 */
export class MidpointPointSnapStrategy extends AbstractPointSnapStrategy {
    /** 策略类型。 */
    public readonly strategyType: PointSnapStrategyType = PointSnapStrategyType.Midpoint;

    /** 普通模式中点捕捉权重。 */
    private static readonly WEIGHT = 80;
    /** 快速捕捉模式权重，确保必定胜出所有普通策略。 */
    private static readonly QUICK_SNAP_WEIGHT = 200;

    /** 当前已显示的中点对象引用，用于切换时自动隐藏。 */
    private lastShownMidPoint: SketchPointObject | null = null;

    /**
     * 最后一次真正悬浮的边缘对象。
     * 说明：鼠标移出后继续显示该边缘的中点，直到移入其他几何体时才切换。
     */
    private lastHoveredEdge: AbstractSketchEdgeObject | null = null;

    /** 快速捕捉模式下当前高亮的边线，用于 clearPreview() 恢复材质。 */
    private quickSnapHighlightedEdge: AbstractSketchEdgeObject | null = null;

    /** 当前吸附高亮的中点对象。 */
    private snappedHighlightedPoint: SketchPointObject | null = null;

    /**
     * 执行中点捕捉求解。
     * @param request 点捕捉请求参数。
     * @returns 线段或圆弧中点捕捉结果；完整圆、完整椭圆或无可用边缘时返回 null。
     */
    public resolve(request: PointSnapRequest): PointSnapResult | null {
        
        if (request.quickSnapMode === QuickSnapMode.Midpoint) {
            return this.resolveQuickSnap(request);
        }

        const transientModelMidpointResult = this.resolveTransientModelMidpoint(request);
        if (transientModelMidpointResult) {
            this.syncSnapHoverState(transientModelMidpointResult.targetObject);
            return transientModelMidpointResult;
        }
        

        // 获取活跃草图组。
        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            this.hideLastMidPoint();
            this.lastHoveredEdge = null;
            this.syncSnapHoverState(null);
            return null;
        }

        const hoveredObject = request.hoveredObject ?? null;

        // 判断 hoveredObject 是否为当前草图支持的边缘类型。
        const isEdge = hoveredObject !== null && (
            isSketchLineObject(hoveredObject) ||
            isSketchArcObject(hoveredObject) ||
            isSketchCircleObject(hoveredObject) ||
            isSketchEllipseObject(hoveredObject)
        );

        if (isEdge && !this.isSnapTargetExcluded(request, hoveredObject)) {
            // 确认属于当前活跃草图组。
            const edges = group.getSketchEdgeObjects();
            const matched = Array.from(edges).find(e => e === hoveredObject) ?? null;
            if (matched !== null) {
                // 移入新边缘时更新 lastHoveredEdge。
                this.lastHoveredEdge = matched;
            }
        }

        // 使用 lastHoveredEdge（粘滞：移出后继续显示，直到换到新边缘）。
        const bestEdge = this.lastHoveredEdge;
        if (bestEdge === null) {
            this.syncSnapHoverState(null);
            return null;
        }
        if (this.isSnapTargetExcluded(request, bestEdge)) {
            this.hideLastMidPoint();
            this.lastHoveredEdge = null;
            this.syncSnapHoverState(null);
            return null;
        }

        // 标记鼠标是否当前真正悬浮在该边缘上（区分粘滞状态）。
        const isCurrentlyHovered = hoveredObject === bestEdge;

        // 线段：显示自带的 midPoint 对象（粘滞：移出后仍可见并可捕捉）。
        if (isSketchLineObject(bestEdge)) {
            const lineResult = this.handleLineMidpoint(bestEdge, request);
            this.syncSnapHoverState(lineResult?.targetObject ?? null);
            return lineResult;
        }

        // 圆弧：检测中点捕捉；仅当真正悬浮时才返回圆弧边跟随指示器。
        if (isSketchArcObject(bestEdge)) {
            const arcResult = this.handleArcMidpoint(bestEdge, request, isCurrentlyHovered);
            this.syncSnapHoverState(arcResult?.targetObject ?? null);
            return arcResult;
        }

        
        // 完整圆和完整椭圆没有唯一中点；进入这两类边缘时只清理旧中点显示，
        // 不生成伪中点结果，让权威最近点策略统一计算曲线投影并显示最近点图标。
        if (isSketchCircleObject(bestEdge) || isSketchEllipseObject(bestEdge)) {
            this.hideLastMidPoint();
            this.syncSnapHoverState(null);
            return null;
        }
        

        this.hideLastMidPoint();
        this.syncSnapHoverState(null);
        return null;
    }

    /**
     * 解析鼠标悬停可见模型边后动态生成的真实曲线中点。
     *
     * @param request 当前点捕捉请求。
     * @returns 像素阈值内的模型边中点捕捉结果；未命中时返回 null。
     */
    private resolveTransientModelMidpoint(request: PointSnapRequest): PointSnapResult | null {
        let bestPoint: SketchPointObject | null = null;
        let bestDistance = this.snapPixelThreshold;

        for (const point of request.transientPointObjects ?? []) {
            if (
                !point.isSketchSessionModelEdgeMidpoint
                || !point.visible
                || this.isSnapTargetExcluded(request, point)
            ) {
                continue;
            }
            const screenPosition = this.projectWorldToScreen(point.getPosition());
            if (!screenPosition) {
                continue;
            }
            const distance = screenPosition.distanceTo(request.screenPosition);
            if (distance < bestDistance) {
                bestDistance = distance;
                bestPoint = point;
            }
        }

        if (!bestPoint) {
            return null;
        }
        const position = bestPoint.getPosition().clone();
        return {
            strategyType: this.strategyType,
            position,
            indicatorPosition: position.clone(),
            targetObject: bestPoint,
            weight: MidpointPointSnapStrategy.WEIGHT,
            showIndicator: true,
        };
    }

    /**
     * 清除快速捕捉高亮预览（恢复边线材质、隐藏中点）。
     */
    public override clearPreview(): void {
        this.reset();
        this.clearQuickSnapHighlight();
    }

    /**
     * 快速捕捉模式求解：全局找最近线段/圆弧，高亮边线，直接捕捉中点。
     * 说明：圆/椭圆无确定中点，不参与快速中点捕捉。
     * @param request 点捕捉请求参数。
     * @returns 最近线段/圆弧的中点捕捉结果；无可用边时返回 null。
     */
    private resolveQuickSnap(request: PointSnapRequest): PointSnapResult | null {
        // 普通模式粘滞状态不影响快速捕捉，先清理。
        this.hideLastMidPoint();
        this.lastHoveredEdge = null;

        const group = this.viewer?.sketchGroupManager?.activeSketchGroup;
        if (!group) {
            this.clearQuickSnapHighlight();
            return null;
        }

        // 全局搜索最近的线段/圆弧（无距离限制）。
        let bestEdge: AbstractSketchEdgeObject | null = null;
        let bestDist = Infinity;

        for (const edge of group.getSketchEdgeObjects()) {
            if (this.isSnapTargetExcluded(request, edge)) {
                continue;
            }
            // 只处理有确定中点的边类型：线段和圆弧。
            if (!isSketchLineObject(edge) && !isSketchArcObject(edge)) {
                continue;
            }
            if ((isSketchLineObject(edge) && edge.isDrawingPreviewLine) || (isSketchArcObject(edge) && edge.isDrawingPreviewArc)) {
                continue;
            }

            // 取中点世界坐标作为距离基准。
            const midWorldPos = isSketchLineObject(edge)
                ? edge.getMidPoint().getPosition()
                : (edge as SketchArcObject).getMidPoint().getPosition();
            const midPoint = isSketchLineObject(edge)
                ? edge.getMidPoint()
                : (edge as SketchArcObject).getMidPoint();
            if (this.isSnapTargetExcluded(request, midPoint)) {
                continue;
            }

            const screenPos = this.projectWorldToScreen(midWorldPos);
            if (!screenPos) continue;

            const dist = screenPos.distanceTo(request.screenPosition);
            if (dist < bestDist) {
                bestDist = dist;
                bestEdge = edge;
            }
        }

        if (bestEdge === null) {
            this.clearQuickSnapHighlight();
            this.syncSnapHoverState(null);
            return null;
        }

        // 高亮最近边（差量更新）。
        if (this.quickSnapHighlightedEdge !== bestEdge) {
            this.clearQuickSnapHighlight();
            this.viewer?.interactionStateManager?.setSketchObjectState(
                bestEdge,
                GeometryInteractionState.Hover,
            );
            this.quickSnapHighlightedEdge = bestEdge;
        }

        // 显示并捕捉中点。
        if (isSketchLineObject(bestEdge)) {
            const midPoint = bestEdge.getMidPoint();
            this.showMidPoint(midPoint);
            this.syncSnapHoverState(midPoint);
            return {
                strategyType: this.strategyType,
                position: midPoint.getPosition().clone(),
                targetObject: midPoint,
                weight: MidpointPointSnapStrategy.QUICK_SNAP_WEIGHT,
                showIndicator: true,
            };
        }

        if (isSketchArcObject(bestEdge)) {
            const midPoint = bestEdge.getMidPoint();
            this.showMidPoint(midPoint);
            this.syncSnapHoverState(midPoint);
            return {
                strategyType: this.strategyType,
                position: midPoint.getPosition().clone(),
                targetObject: midPoint,
                weight: MidpointPointSnapStrategy.QUICK_SNAP_WEIGHT,
                showIndicator: true,
            };
        }

        return null;
    }

    /**
     * 处理线段中点的显示与捕捉（两阶段逻辑）。
     * @param line 悬停的线段对象。
     * @param request 点捕捉请求参数。
     * @returns 线段中点或靠近点捕捉结果。
     */
    private handleLineMidpoint(line: SketchLineObject, request: PointSnapRequest): PointSnapResult | null {
        // 获取线段自带的中点对象。
        const midPoint = line.getMidPoint();
        if (this.isSnapTargetExcluded(request, midPoint)) {
            this.hideLastMidPoint();
            return null;
        }

        // 切换中点：隐藏上一个，显示当前。
        this.showMidPoint(midPoint);

        // 获取中点世界坐标。
        const midPosition = midPoint.getPosition().clone();

        // 判断鼠标是否已移入中点位置附近。
        const midScreenPos = this.projectWorldToScreen(midPosition);
        const nearMidpoint = midScreenPos !== null
            && midScreenPos.distanceTo(request.screenPosition) < this.snapPixelThreshold;

        if (nearMidpoint) {
            // 鼠标在中点附近：返回中点捕捉结果，指示器显示在中点位置。
            return {
                strategyType: this.strategyType,
                position: midPosition,
                targetObject: midPoint,
                weight: MidpointPointSnapStrategy.WEIGHT,
                showIndicator: true,
            };
        }

        // 鼠标在线段上但未到中点：无捕捉命中，返回 null。
        return null;
    }

    
    /**
     * 处理圆弧中点的显示与捕捉（两阶段逻辑）。
     * @param arc 悬停的圆弧对象。
     * @param request 点捕捉请求参数。
     * @param isCurrentlyHovered 当前鼠标是否真实悬浮在该圆弧边缘上。
     * @returns 圆弧中点捕捉结果或圆弧边跟随指示器结果。
     */
    private handleArcMidpoint(
        arc: SketchArcObject,
        request: PointSnapRequest,
        isCurrentlyHovered: boolean,
    ): PointSnapResult | null {
        // 获取圆弧默认中点对象，并保持与线段一致的可见化策略。
        const midPoint = arc.getMidPoint();
        if (this.isSnapTargetExcluded(request, midPoint)) {
            this.hideLastMidPoint();
            return null;
        }
        this.showMidPoint(midPoint);

        // 获取中点世界坐标。
        const midPosition = midPoint.getPosition().clone();

        // 判断鼠标是否已移入圆弧中点位置附近。
        const midScreenPos = this.projectWorldToScreen(midPosition);
        const nearMidpoint = midScreenPos !== null
            && midScreenPos.distanceTo(request.screenPosition) < this.snapPixelThreshold;

        if (nearMidpoint) {
            // 鼠标在圆弧中点附近：返回中点捕捉结果，targetObject 必须为中点对象。
            return {
                strategyType: this.strategyType,
                position: midPosition,
                targetObject: midPoint,
                weight: MidpointPointSnapStrategy.WEIGHT,
                showIndicator: true,
            };
        }

        // 仅当真正悬浮时才返回圆弧边跟随指示器（粘滞状态下不跟随鼠标）。
        if (isCurrentlyHovered) {
            return {
                strategyType: PointSnapStrategyType.OnArc,
                position: request.worldPosition.clone(),
                targetObject: arc,
                weight: MidpointPointSnapStrategy.WEIGHT,
                showIndicator: true,
                indicatorPosition: request.worldPosition.clone(),
            };
        }

        return null;
    }

    /**
     * 显示目标中点对象，并在切换时隐藏上一个中点对象。
     * @param midPoint 需要显示的中点对象。
     */
    private showMidPoint(midPoint: SketchPointObject): void {
        // 切换中点对象时，先隐藏旧对象，确保场景中只保留一个可见中点。
        if (this.lastShownMidPoint !== midPoint) {
            this.hideLastMidPoint();
            this.lastShownMidPoint = midPoint;
        }
        // 粘滞场景下中点可能被其他策略暂时隐藏，这里强制恢复可见。
        this.keepMidPointVisible(midPoint);
    }
    

    /**
     * 隐藏上一个已显示的中点对象。
     */
    private hideLastMidPoint(): void {
        if (this.lastShownMidPoint) {
            /**
             * 当中点已经被复用为端点时，不允许在策略清理阶段直接隐藏。
             * 否则会出现“新建线端点存在但不显示”的回归问题。
             */
            this.viewer?.interactionStateManager?.setSketchObjectVisibilityHint?.(this.lastShownMidPoint, false);
            if (!this.lastShownMidPoint.isEndPoint) {
                this.lastShownMidPoint.visible = false;
            }
            this.lastShownMidPoint = null;
        }
    }

    /**
     * 清除快速捕捉高亮的边线，恢复默认材质。
     */
    private clearQuickSnapHighlight(): void {
        if (this.quickSnapHighlightedEdge !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.quickSnapHighlightedEdge);
            this.quickSnapHighlightedEdge = null;
        }

        if (this.snappedHighlightedPoint !== null) {
            this.viewer?.interactionStateManager?.restoreSketchObjectState(this.snappedHighlightedPoint);
            this.snappedHighlightedPoint = null;
        }
    }

    /**
     * 同步当前命中的中点对象移入材质状态。
     * @param targetObject 当前捕捉目标对象。
     */
    private syncSnapHoverState(targetObject: PointSnapResult['targetObject'] | null): void {
        const point = targetObject instanceof SketchPointObject ? targetObject : null;
        if (point === null) {
            if (this.snappedHighlightedPoint !== null) {
                this.viewer?.interactionStateManager?.restoreSketchObjectState(this.snappedHighlightedPoint);
                this.snappedHighlightedPoint = null;
            }
            return;
        }

        if (this.snappedHighlightedPoint !== point) {
            if (this.snappedHighlightedPoint !== null) {
                this.viewer?.interactionStateManager?.restoreSketchObjectState(this.snappedHighlightedPoint);
            }
            this.snappedHighlightedPoint = point;
        }

        
        // Hover 写入会刷新点对象默认显示，中点默认隐藏，因此写入前后都要保持可见提示。
        this.keepMidPointVisible(point);
        this.viewer?.interactionStateManager?.setSketchObjectState(
            point,
            GeometryInteractionState.Hover,
        );
        this.keepMidPointVisible(point);
        
    }

    
    /**
     * 保持中点对象在当前捕捉帧内可见。
     * 说明：
     * 1. 中点默认隐藏，但捕捉策略需要在悬停或命中期间临时显示；
     * 2. 交互态刷新会重新套用默认显隐规则，因此需要写入手动可见性提示；
     * 3. 这里仍显式设置 `visible = true`，保证未启动交互态管理器的测试或轻量运行环境也能显示。
     * @param midPoint 需要保活的中点对象。
     */
    private keepMidPointVisible(midPoint: SketchPointObject): void {
        this.viewer?.interactionStateManager?.setSketchObjectVisibilityHint?.(midPoint, true);
        midPoint.visible = true;
    }
    

    /**
     * 重置中点显示状态（普通模式清理）。
     * 说明：在绘制结束或策略停用时调用，确保不残留可见的中点对象。
     */
    public reset(): void {
        this.hideLastMidPoint();
        this.lastHoveredEdge = null;
        this.syncSnapHoverState(null);
    }
}


